// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemStyle, GridWidths } from "@insite/mobius/GridItem";
import { CustomFieldDefinition } from "@steiner/App/CustomField/CustomFieldDefinition";
import { DivWrapper } from "@steiner/XDL/constants";
import { layoutContainer } from "@steiner/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    customCSS = "customCSS",
    // XNG-Changes
    containerId = "containerId",
    applyLayoutView = "applyLayoutView",
    isFullHeight = "isFullHeight",
    backgroundColor = "backgroundColor",
    isBackgroundFullWidth = "isBackgroundFullWidth",
}

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.containerId]: string;
        [fields.applyLayoutView]: boolean;
        [fields.isFullHeight]: boolean;
        [fields.backgroundColor]: string;
        [fields.isBackgroundFullWidth]: boolean;
    };
}

const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [12, 12, 1, 1, 1],
    2: [12, 12, 2, 2, 2],
    3: [12, 12, 3, 3, 3],
    4: [12, 12, 4, 4, 4],
    5: [12, 12, 5, 5, 5],
    6: [12, 12, 6, 6, 6],
    7: [12, 12, 7, 7, 7],
    8: [12, 12, 8, 8, 8],
    9: [12, 12, 9, 9, 9],
    10: [12, 12, 10, 10, 10],
    11: [12, 12, 11, 11, 11],
    12: 12,
};

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            isFullWidth,
            fullWidthStylingType,
            fullWidthColor,
            fullWidthImage,
            fullWidthImageFocalPoint,
            customCSS,
            // XNG-Changes
            applyLayoutView,
            isFullHeight,
            containerId,
            backgroundColor,
            isBackgroundFullWidth,
        },
        id,
    } = props;

    const columnElements: JSX.Element[] = [];

    for (let x = 0; x < columns.length; x += 1) {
        const align = alignment[x] || "topLeft";
        const [vertical, horizontal] = align.split(/(?=[A-Z])/);
        const verticalAlign = vertical === "top" ? "flex-start" : vertical === "middle" ? "center" : "flex-end";
        const horizontalAlign = !horizontal
            ? "flex-start"
            : horizontal.toLowerCase() === "left"
              ? "flex-start"
              : horizontal.toLowerCase() === "center"
                ? "center"
                : "flex-end";
        const innerItemHorizontalAlign = !horizontal
            ? "left"
            : horizontal.toLowerCase() === "left"
              ? "left"
              : horizontal.toLowerCase() === "center"
                ? "center"
                : "right";

        columnElements.push(
            <GridItem
                key={x}
                width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                css={css`
                    display: flex;
                    align-items: ${verticalAlign};
                    justify-content: ${horizontalAlign};

                    & > div {
                        text-align: ${innerItemHorizontalAlign};
                    }
                `}
            >
                <Zone contentId={id} zoneName={`Content${x}`} />
            </GridItem>,
        );
    }

    const fullWidthBackgroundCss = !isFullWidth
        ? undefined
        : fullWidthStylingType === "solidColor"
          ? css`
                background-color: ${fullWidthColor};
            `
          : fullWidthStylingType === "image"
            ? css`
                  background-image: url("${parseImageUrl(fullWidthImage).url}");
                  background-size: cover;
                  ${getFocalPointStyles(fullWidthImageFocalPoint)}
              `
            : undefined;

    // <xng-change>
    const finalContainerCss = css`
        ${applyLayoutView &&
        css`
            ${layoutContainer}
        `}
        ${isBackgroundFullWidth &&
        backgroundColor &&
        css`
            position: relative;
            &::before {
                content: "";
                position: absolute;
                width: 100vw;
                height: 100%;
                background-color: ${backgroundColor};
                left: 50%;
                transform: translateX(-50%);
            }
        `}
    `;

    const finalCss = css`
        margin-left: auto;
        margin-right: auto;
        background: ${!isBackgroundFullWidth ? backgroundColor : "transparent"};
        position: relative;

        ${fullWidthBackgroundCss}
        ${customCSS}
    `;

    const finalOffsetCss = css`
        ${isFullHeight &&
        css`
            align-items: stretch;
            & > ${GridItemStyle} > ${DivWrapper} {
                &,
                & > div {
                    height: 100%;
                }
            }
        `}
    `;

    return (
        <StyledWrapper css={finalContainerCss} className={containerId}>
            <GridContainer gap={gap || 0} isFullWidth={isFullWidth} css={finalCss} offsetCss={finalOffsetCss}>
                {columnElements}
            </GridContainer>
        </StyledWrapper>
    );
    // </xng-change>
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const definition: WidgetDefinition<CustomFieldDefinition> = {
    group: "Basic",
    icon: "distribute-spacing-vertical",
    fieldDefinitions: [
        {
            name: fields.columns,
            displayName: "Layout",
            editorTemplate: "ColumnsField",
            defaultValue: [4, 4, 4],
            fieldType: "General",
            sortOrder: 1,
            validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
        },
        {
            name: fields.reflow,
            displayName: "Use default reflow for responsive screens sizes",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "General",
            sortOrder: 10,
        },
        {
            name: fields.gap,
            displayName: "Gap Between Items",
            editorTemplate: "IntegerField",
            defaultValue: 0,
            fieldType: "General",
            max: 1000,
            min: 0,
            sortOrder: 2,
        },
        // <xng-change>
        // {
        //     name: fields.isFullWidth,
        //     displayName: "Full Width",
        //     editorTemplate: "CheckboxField",
        //     defaultValue: false,
        //     fieldType: "General",
        //     isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
        //     sortOrder: 3,
        // },
        // {
        //     name: fields.fullWidthStylingType,
        //     displayName: "Styling",
        //     editorTemplate: "RadioButtonsField",
        //     options: [
        //         {
        //             displayName: "Solid Color",
        //             value: "solidColor",
        //         },
        //         {
        //             displayName: "Image",
        //             value: "image",
        //         },
        //     ],
        //     defaultValue: "solidColor",
        //     horizontal: true,
        //     fieldType: "General",
        //     isVisible: ({ fields: { isFullWidth } }, _advancedFeaturesEnabled, theme) =>
        //         !!theme?.content?.isFullWidth && !!isFullWidth,
        //     sortOrder: 4,
        // },
        // {
        //     name: fields.fullWidthColor,
        //     displayName: "Color",
        //     editorTemplate: "ColorPickerField",
        //     defaultValue: "transparent",
        //     fieldType: "General",
        //     isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
        //         !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "solidColor",
        //     sortOrder: 5,
        // },
        // {
        //     name: fields.fullWidthImage,
        //     displayName: "Image",
        //     editorTemplate: "ImagePickerField",
        //     defaultValue: "",
        //     fieldType: "General",
        //     isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
        //         !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
        //     sortOrder: 6,
        // },
        // {
        //     name: fields.fullWidthImageFocalPoint,
        //     displayName: "Focal Point",
        //     editorTemplate: "DropDownField",
        //     options: [
        //         { displayName: "Top Left", value: "topLeft" },
        //         { displayName: "Top Center", value: "topCenter" },
        //         { displayName: "Top Right", value: "topRight" },
        //         { displayName: "Center Left", value: "centerLeft" },
        //         { displayName: "Center", value: "center" },
        //         { displayName: "Center Right", value: "centerRight" },
        //         { displayName: "Bottom Left", value: "bottomLeft" },
        //         { displayName: "Bottom Center", value: "bottomCenter" },
        //         { displayName: "Bottom Right", value: "bottomRight" },
        //     ],
        //     defaultValue: "center",
        //     fieldType: "General",
        //     hideEmptyOption: true,
        //     isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
        //         !!theme?.content?.isFullWidth && !!isFullWidth && fullWidthStylingType === "image",
        //     sortOrder: 7,
        // },
        {
            name: fields.alignment,
            displayName: "Content Alignment",
            editorTemplate: "StrColumnAlignmentField",
            defaultValue: "topLeft",
            fieldType: "General",
            tab: advancedTab,
        },
        {
            name: fields.containerId,
            displayName: "Container Id",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            tab: advancedTab,
        },
        {
            name: fields.applyLayoutView,
            displayName: "Apply Layout View",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: advancedTab,
        },
        {
            name: fields.isFullHeight,
            displayName: "Show all child Columns with the same height",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: advancedTab,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            tab: advancedTab,
        },
        {
            name: fields.isBackgroundFullWidth,
            displayName: "Make Background Color Full Width",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
            tab: advancedTab,
            isVisible: ({ fields: { backgroundColor } }) => !!backgroundColor,
        },
        // </xng-change>
        {
            name: "customCSS",
            displayName: "Custom CSS",
            editorTemplate: "CodeField",
            fieldType: "General",
            tab: advancedTab,
            defaultValue: defaultCustomCss,
            isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            validate: value => {
                if (value === undefined || value === null) {
                    return "";
                }

                const result = cssLinter.parse(value, { silent: true });
                if (result?.stylesheet?.parsingErrors) {
                    // the error output at this time only has room for one line so we just show the first error
                    return result.stylesheet.parsingErrors.length <= 0
                        ? ""
                        : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                }

                return "Unable to parse Css";
            },
            options: {
                mode: "css",
                lint: true,
                autoRefresh: true,
            },
        },
    ],
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: Row,
    definition,
};

export default widgetModule;
